import { DOMAIN } from "@/app/lib/enums";
import { getSharedCookieRootDomainFromHost } from "@/app/lib/tenant";

export const TOKEN_KEY = "access_token";
export const SHARED_AUTH_TOKEN_KEY = "auth_token";

export const SHARED_AUTH_COOKIE_MAX_AGE_SECONDS = 30 * 60;

const getCookieDomain = (domain: DOMAIN): string => `.${domain}`;

export const getAccessToken = (): string | null => {
   return safeCookie.get(TOKEN_KEY);
};

export const getAccessTokenAsync = async (): Promise<string> => {
   try {
      // Server-side
      if (typeof window === "undefined") {
         const { cookies } = require("next/headers");
         const cookieStore = await cookies();
         return cookieStore.get(TOKEN_KEY)?.value ?? "";
      }
      // Client-side
      return getAccessToken() ?? "";
   } catch (error) {
      console.warn("Failed to get token from cookies:", error);
      return "";
   }
};

/**
 * Get access token from NextRequest object (for API routes)
 * @param request - NextRequest object from API route
 * @returns Token string or empty string if not found
 */
export const getAccessTokenFromRequest = (request: any): string => {
   try {
      return request.cookies.get(TOKEN_KEY)?.value ?? "";
   } catch (error) {
      console.warn("Failed to get token from request cookies:", error);
      return "";
   }
};

export const getSharedAuthTokenFromRequest = (request: any): string => {
   try {
      return request.cookies.get(SHARED_AUTH_TOKEN_KEY)?.value ?? "";
   } catch (error) {
      console.warn("Failed to get shared auth token from request cookies:", error);
      return "";
   }
};

export const getSharedAuthCookieDomain = (host?: string | null): string | undefined => {
   const rootDomain = getSharedCookieRootDomainFromHost(host);
   return rootDomain ? getCookieDomain(rootDomain) : undefined;
};

export const setAccessToken = (token: string): void => {
   safeCookie.set(TOKEN_KEY, token);
};

export const removeAccessToken = (): void => {
   if (typeof window !== "undefined") {
      const sharedDomain = getSharedAuthCookieDomain(window.location.host);
      if (sharedDomain) {
         safeCookie.remove(TOKEN_KEY, sharedDomain);
      }
   }
   safeCookie.remove(TOKEN_KEY);
   safeCookie.clear();
};

export const removeSharedAuthToken = (): void => {
   if (typeof window === "undefined") return;

   const sharedDomain = getSharedAuthCookieDomain(window.location.host);
   safeCookie.remove(SHARED_AUTH_TOKEN_KEY);
   if (sharedDomain) {
      safeCookie.remove(SHARED_AUTH_TOKEN_KEY, sharedDomain);
   }
};

export const safeCookie = {
   set: (name: string, value: string, days: number = 30): void => {
      const isClient = typeof window !== "undefined";
      if (!isClient) return;
      const d = new Date();
      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      d.setTime(d.getTime() + days * millisecondsPerDay);
      const expires = "expires=" + d.toUTCString();
      document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax;domain=${window.location.hostname}`;
   },

   remove: (name: string, domain?: string): void => {
      if (typeof window === "undefined") return;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      if (domain) {
         document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
      }
   },

   clear: (): void => {
      if (typeof window === "undefined") return;
      const domain = window.location.hostname;
      document.cookie.split(";").forEach(function (c) {
         const cookieName = c.replace(/^ +/, "").split("=")[0];
         document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
         document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
      });
   },

   get: (name: string): string | null => {
      if (typeof window === "undefined") return null;
      const nameEQ = name + "=";
      const ca = document.cookie.split(";");

      for (let i = 0; i < ca.length; i++) {
         let c = ca[i];
         while (c.charAt(0) === " ") c = c.substring(1, c.length);
         if (c.indexOf(nameEQ) === 0) {
            const value = c.substring(nameEQ.length, c.length);
            return value;
         }
      }
      return null;
   }
};
